/**
 * =================================================================
 * API Service Layer for AI Estimating Assistant Web App
 * =================================================================
 *
 * This file centralizes all communication with the FastAPI backend.
 * It uses Axios for HTTP requests and includes interceptors for
 * automatically handling authentication tokens and errors.
 *
 * Features:
 * - Pre-configured Axios instance.
 * - Automatic injection of Firebase Auth Bearer token.
 * - Global error handling for API responses.
 * - Typed functions for all backend endpoints.
 * - A single, organized `BackendAPI` object for easy use.
 */

import axios from 'axios';

/**
 * Backend base URL - Always use HTTPS in production
 * Force HTTPS to prevent mixed content errors
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

if (import.meta.env.DEV) {
  console.info('[api] Using backend URL:', BASE_URL);
}

import { auth } from './firebase';

import type { ApiError } from '../types';
import { baseVersion, buildApiError, rememberPayloadVersion } from './estimateChanged';
import {
  Project,
  NewProjectForm,
  FieldNote,
  GenerateScopePayload,
  GeneratedScope,
  ChatbotResponse,
  UserPreferences,
  ScopeConfig,
  GenerationPreferences,
  AnalysisStats,
  CodeProfile,
  RoomTemplate,
  ScopeSuggestion,
  RoomTypeInfo,
  ScopeGenerationRequest,
  ScopeGenerationResponse,
  BatchStatus,
  UploadResponse,
  CompletenessReport,
  ParsedEstimateResponse,
  EstimateHeaderMetadata,
  ProjectMatchResponse,
  RoomScopeRequest,
  RoomScopeDetailResponse,
  RoomChecklist,
  ChecklistPrompt,
  EsxGenerationResult,
  BootstrapResponse,
  AgreementResponse,
  CreditsResponse,
  ImportSummary,
  PreflightResponse,
  EnqueueResponse,
  JobStatusResponse,
  EnhanceNarratives,
  EnhanceContext,
  EnhanceRun,
  EnhanceResolution,
  EnhanceSessionResponse,
  EnhanceApplyResponse,
  EnhanceRevertResponse,
} from '../types';

// Re-export for convenience
export type { AnalysisStats, CodeProfile, RoomTemplate, RoomTypeInfo };
import { meApi } from './meApi';
import { grantsApi } from './grantsApi';

// --- 1. Axios Instance Configuration ---

// Exported for the interceptor tests (B-910); application code goes through BackendAPI.
export const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- 2. Axios Interceptors ---

/**
 * Request Interceptor:
 * Attaches the Firebase Auth token to every outgoing request if available.
 */
api.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Could not get Firebase token:', error);
      }
    } else if (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true') {
      // Reason: Dev bypass sets a fake user in Zustand but auth.currentUser is null
      // since nobody actually signed into Firebase. Send a marker header so the
      // backend (with DISABLE_AUTH_FOR_TESTING=true) can accept the request.
      config.headers.Authorization = 'Bearer dev-bypass-token';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/** Convert an axios error into an Error that carries the HTTP status (so callers can branch on 402/413/403). */
export const extractApiError = (error: any): ApiError => buildApiError(error, BASE_URL);

/**
 * Response Interceptor:
 * Handles global API errors and standardizes the response format.
 */
api.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    rememberPayloadVersion(response.data);
    return response.data;
  },
  async (error) => {
    // B-910: a 401 on a cold load is usually a STALE token, not a signed-out user.
    // `getIdToken()` hands back the cached one and only refreshes inside its own
    // expiry window, so a browser that has been asleep sends a token the backend
    // rejects with "Firebase ID token has expired". Force a refresh and retry ONCE.
    // Without this the failure is permanent for anything that does not re-fetch:
    // the dashboard recovered on the next navigation, the role fetch never did,
    // and the admin tools stayed hidden for the whole session.
    const config = error?.config;
    if (error?.response?.status === 401 && config && !config.__retriedAfterRefresh) {
      const user = auth.currentUser;
      if (user) {
        config.__retriedAfterRefresh = true;
        try {
          const fresh = await user.getIdToken(true);
          config.headers = { ...(config.headers || {}), Authorization: `Bearer ${fresh}` };
          return api.request(config);
        } catch {
          // fall through to the normal error path — a refresh that fails means
          // the session really is gone, and that must not be retried in a loop
        }
      }
    }
    return normalError(error);
  }
);

/**
 * The normal failure path for any status outside 2xx.
 *
 * If `error.response` is undefined it usually means a network failure (CORS,
 * DNS, server down, etc.) — say so, because the message the user sees for that
 * is otherwise indistinguishable from a real backend error.
 */
function normalError(error: any) {
  if (!error?.response) {
    console.error('[API] Network/CORS error:', error?.message);
  } else {
    console.error('[API] Error response:', error.response);
  }
  return Promise.reject(extractApiError(error));
}

// --- 3. API Service Definition ---

export const BackendAPI = {
  // --- Project Management ---

  getProjects: (): Promise<Project[]> => api.get('/user/projects/'),

  getProject: (id: string): Promise<Project> => api.get(`/user/projects/${id}`),

  createProject: (data: NewProjectForm): Promise<Project> =>
    api.post('/user/projects/', data),

  /**
   * Fuzzy-match an estimate's header_metadata against existing projects.
   * Used after a PDF parse to power the create/match confirmation dialog.
   * Falls back gracefully on error — the page just skips the dialog.
   */
  matchProject: (header: EstimateHeaderMetadata): Promise<ProjectMatchResponse> =>
    api.post('/user/projects/match', header),

  updateProject: (id: string, data: Partial<Project>): Promise<Project> =>
    api.put(`/user/projects/${id}`, data),

  deleteProject: (id: string): Promise<void> =>
    api.delete(`/user/projects/${id}`),

  /**
   * Archives a project by setting its status to 'archived'.
   *
   * Args:
   *   id (string): The project ID to archive.
   *
   * Returns:
   *   Promise<Project>: The updated project.
   */
  archiveProject: (id: string): Promise<Project> =>
    api.put(`/user/projects/${id}`, { status: 'archived' }),

  /**
   * Fetches the latest scope for a project.
   *
   * Args:
   *   projectId (string): The project ID.
   *
   * Returns:
   *   Promise<object | null>: The latest scope data, or null if none exists.
   */
  getLatestProjectScope: async (projectId: string): Promise<Record<string, unknown> | null> => {
    try {
      return await api.get(`/user/projects/${projectId}/scopes/latest`);
    } catch {
      // Reason: 404 means no scope exists yet, which is a normal state
      return null;
    }
  },

  /**
   * Gets the caller's saved Scope Builder form draft for a project.
   *
   * Args:
   *   projectId (string): The project ID.
   *
   * Returns:
   *   Promise<object | null>: { draft, updated_at }, or null when no draft exists.
   */
  getScopeDraft: async (
    projectId: string,
  ): Promise<{ draft: Record<string, unknown>; updated_at: string } | null> => {
    try {
      return await api.get(`/user/projects/${projectId}/scope-draft`);
    } catch {
      // Reason: 404 means no draft yet — a normal state.
      return null;
    }
  },

  /**
   * Upserts the caller's Scope Builder form draft for a project. Saving a
   * draft is free — no AI generation involved.
   *
   * Args:
   *   projectId (string): The project ID.
   *   draft (object): The versioned draft payload (client-defined shape).
   *
   * Returns:
   *   Promise<object>: { draft, updated_at }.
   */
  putScopeDraft: (
    projectId: string,
    draft: Record<string, unknown>,
  ): Promise<{ draft: Record<string, unknown>; updated_at: string }> =>
    api.put(`/user/projects/${projectId}/scope-draft`, { draft }),

  /**
   * Saves a scope to a project.
   *
   * Args:
   *   projectId (string): The project ID.
   *   scopeData (object): The scope data to save.
   *   source (string): Optional source identifier (e.g., 'web', 'mobile').
   *
   * Returns:
   *   Promise<object>: The saved scope record.
   */
  saveProjectScope: (
    projectId: string,
    scopeData: object,
    source?: string,
    ruleTrace?: Array<Record<string, unknown>>,
  ): Promise<Record<string, unknown>> =>
    api.post(`/user/projects/${projectId}/scopes`, {
      scope_data: scopeData,
      source,
      ...(ruleTrace && ruleTrace.length > 0 ? { rule_trace: ruleTrace } : {}),
    }),

  /**
   * Generate an importable Xactimate .ESX from a persisted scope.
   *
   * Uses the JSON endpoint (returns base64 + adapter report) because the
   * axios response interceptor unwraps `response.data`, which would fight a
   * binary octet-stream body. Decode + download via `lib/esxDownload`.
   */
  downloadScopeEsx: (
    scopeId: string,
    projectNameOverride?: string,
  ): Promise<EsxGenerationResult> =>
    api.post('/esx/generate-from-scope/json', {
      scope_id: scopeId,
      project_name_override: projectNameOverride,
    }),

  /**
   * Faithfully import a carrier PDF estimate and get back an importable ESX
   * (base64 + adapter report). No enhancement — replicates the source scope.
   */
  generateEsxFromPdf: (file: File, maxItems?: number): Promise<EsxGenerationResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const qs = maxItems ? `?max_items=${maxItems}` : '';
    return api.post(`/esx/generate-from-pdf/json${qs}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Export the PERSISTED (displayed) parse to an importable ESX — WYSIWYG.
   *
   * W1 convergence: instead of re-uploading the file to a second parser, this
   * exports the exact parse the Supplement page already showed, referenced by
   * the `parse_hash` from the parse/analyze step. Returns the same shape as
   * generateEsxFromPdf (filename, esx_b64, report, scope_preview, run_ref).
   */
  generateEsxFromParse: (
    parseHash: string,
    projectNameOverride?: string,
    enableSketch?: boolean,
    confirmCredits?: number,
    // B-400: FEMA/NFIP flood coverage profile. `undefined` lets the backend
    // auto-detect from the parsed loss type (flood losses default ON);
    // true/false is the user's explicit toggle override.
    useFloodProfile?: boolean,
    version?: 'original', // PRP-MCP-8: export the import's Original (credit rules unchanged)
  ): Promise<EsxGenerationResult> =>
    api.post('/esx/generate-from-parse', {
      version,
      parse_hash: parseHash,
      project_name_override: projectNameOverride,
      enable_sketch: enableSketch,
      // The cost the user confirmed; the backend rejects with 409 if its
      // server-computed cost has since changed (price-change guard).
      confirm_credits: confirmCredits,
      use_flood_profile: useFloodProfile,
    }),

  // --- Notes Management ---
  // Backend mounts notes at /user/projects/notes/{projectId}

  getNotesForProject: (projectId: string): Promise<FieldNote[]> =>
    api.get(`/user/projects/notes/${encodeURIComponent(projectId)}`),

  /** A fresh 5-minute signed link to a photo note's image (B-1244). The notes list already
   *  carries one per photo (`image_url`); call this when that link expired or was not signed. */
  getNoteImageUrl: (projectId: string, noteId: string): Promise<{ url: string; expires_at: string | null }> =>
    api.get(`/user/projects/notes/${encodeURIComponent(projectId)}/${encodeURIComponent(noteId)}/image-url`),

  createNote: (
    projectId: string,
    data: Partial<FieldNote>
  ): Promise<FieldNote> => api.post(`/user/projects/notes/${projectId}`, data),

  updateNote: (projectId: string, noteId: string, data: Partial<FieldNote>): Promise<FieldNote> =>
    api.put(`/user/projects/notes/${projectId}/${noteId}`, data),

  deleteNote: (projectId: string, noteId: string): Promise<void> =>
    api.delete(`/user/projects/notes/${projectId}/${noteId}`),

  uploadVoiceNote: (projectId: string, title: string, file: File): Promise<FieldNote> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    return api.post(`/user/projects/notes/${projectId}/voice`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  uploadPhotoNote: (projectId: string, title: string, file: File): Promise<FieldNote> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    return api.post(`/user/projects/notes/${projectId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // --- Photo Management ---

  uploadPhoto: async (
    projectId: string,
    file: File
  ): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    // Note: The endpoint for photo upload needs to be confirmed from backend implementation.
    // This is a placeholder.
    return api.post(`/user/projects/${projectId}/photos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // --- Chatbot & Scope Generation ---

  sendMessageToChatbot: async (
    payload: FormData
  ): Promise<ChatbotResponse> => {
    // FormData is sent directly, as it includes the question and optional image.
    // The interceptor will handle the auth token.
    return api.post('/chatbot/', payload, {
      headers: {
        // Axios will automatically set the correct multipart header with boundary
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  generateScope: (payload: GenerateScopePayload): Promise<GeneratedScope> =>
    api.post('/intelligent-scope/generate', payload),

  // --- User Profile & Preferences ---

  getUserPreferences: (): Promise<UserPreferences> =>
    api.get('/intelligent-scope/profiles/me'),

  updateScopeConfig: (config: ScopeConfig): Promise<UserPreferences> =>
    api.put('/intelligent-scope/profiles/me/scope-config', config),

  updateGenerationPrefs: (generation: GenerationPreferences): Promise<UserPreferences> =>
    api.put('/intelligent-scope/profiles/me/generation', generation),

  // --- Utility & Health Check ---

  getHealth: (): Promise<{ status: string }> => api.get('/health/'),

  // --- Analysis Engine ---

  getAnalysisStats: (): Promise<AnalysisStats> =>
    api.get('/analysis/stats'),

  getCodeProfile: (xactCode: string): Promise<CodeProfile | null> =>
    api.get(`/analysis/codes/${encodeURIComponent(xactCode)}`),

  getTopCodes: (n = 20): Promise<{ codes: CodeProfile[]; total_codes: number }> =>
    api.get(`/analysis/codes/top?n=${n}`),

  getRoomTemplate: (roomType: string, damageType?: string): Promise<RoomTemplate | null> => {
    let url = `/analysis/room-template/${encodeURIComponent(roomType)}`;
    if (damageType) url += `?damage_type=${encodeURIComponent(damageType)}`;
    return api.get(url);
  },

  getScopeSuggestions: (roomType: string, damageType?: string, minFrequency?: number): Promise<ScopeSuggestion[]> => {
    let url = `/analysis/suggest/${encodeURIComponent(roomType)}`;
    const params = new URLSearchParams();
    if (damageType) params.append('damage_type', damageType);
    if (minFrequency !== undefined) params.append('min_frequency', String(minFrequency));
    const qs = params.toString();
    if (qs) url += `?${qs}`;
    return api.get(url);
  },

  getRoomTypes: (): Promise<{ room_types: RoomTypeInfo[]; total: number }> =>
    api.get('/analysis/room-types'),

  // --- Scope Generation ---

  generateScopeEnhanced: (request: ScopeGenerationRequest): Promise<ScopeGenerationResponse> =>
    api.post('/scope/generate', request),

  /**
   * Supplement an existing saved scope with additional field notes.
   * Returns only the DELTA — items missing from the existing scope.
   */
  supplementScope: (request: {
    scope_id: string;
    notes: string;
    damage_type?: string;
    jurisdiction?: string;
    property_type?: string;
    cat3_toggle?: boolean;
  }): Promise<ScopeGenerationResponse> =>
    api.post('/scope/supplement', request),

  // --- Admin Upload ---

  uploadEstimateFiles: async (
    files: File[],
    onProgress?: (percent: number) => void
  ): Promise<UploadResponse> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post('/admin/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    });
  },

  getBatchStatus: async (batchId: string): Promise<BatchStatus> =>
    api.get(`/admin/upload/status/${batchId}`),

  // --- Interactive Scope Generation ---

  /**
   * Generate scope for a single room via the per-room endpoint.
   */
  generateRoomScope: async (request: RoomScopeRequest): Promise<RoomScopeDetailResponse> =>
    api.post('/scope/generate-room', request),

  /**
   * Get the full checklist for a room type.
   */
  getChecklist: async (roomType: string): Promise<RoomChecklist> =>
    api.get(`/scope/checklist/${encodeURIComponent(roomType)}`),

  /**
   * Get unmentioned checklist prompts for a room type given a description.
   */
  getChecklistPrompts: async (roomType: string, description: string): Promise<ChecklistPrompt[]> =>
    api.post('/scope/checklist-prompts', { room_type: roomType, description }),

  // --- Completeness Engine ---

  analyzeEstimate: async (
    file: File,
    claimType?: string,
    aggressiveness: string = 'moderate',
    jurisdiction?: string,
  ): Promise<CompletenessReport> => {
    const formData = new FormData();
    formData.append('file', file);
    if (claimType) formData.append('claim_type', claimType);
    formData.append('aggressiveness', aggressiveness);
    if (jurisdiction) formData.append('jurisdiction', jurisdiction);
    return api.post('/completeness/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Parse an estimate file WITHOUT running completeness analysis.
   * Powers the "verify parse first" flow — returns a ParsedEstimate
   * the user can inspect, plus a parse_hash to pass to /analyze-cached.
   *
   * Internally: enqueues via POST /completeness/parse-async (202), then
   * polls GET /jobs/{job_id} with 1s→3s backoff until ready or failed.
   * Signature and return type are unchanged so all callers keep working.
   */
  /**
   * Free pre-scan of an upload (scanned/vector, header metadata, ~N items,
   * export cost, project match). Consumes no credit, persists nothing.
   */
  preflightEstimate: async (file: File): Promise<PreflightResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/completeness/preflight', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Enqueue a background parse and return immediately. The importJobsStore (or
   * the parseEstimate poll below) owns the wait. Options carry the chosen
   * project and sketch toggle from the PreImportDialog.
   */
  enqueueParse: async (
    file: File,
    opts?: { projectId?: string; enableSketch?: boolean },
  ): Promise<EnqueueResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    // Send fields the endpoint actually reads (project_id: Form(None),
    // enable_sketch: Form(False)). project_id only when set; enable_sketch
    // always, as the string the bool form-parser expects.
    if (opts?.projectId) formData.append('project_id', opts.projectId);
    formData.append('enable_sketch', String(!!opts?.enableSketch));
    return api.post('/completeness/parse-async', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getJobStatus: async (jobId: string): Promise<JobStatusResponse> =>
    api.get(`/jobs/${jobId}`),

  // Refactor: enqueue (with options) then poll to a terminal state. Preserves
  // the blocking signature so existing fallback callers keep working.
  parseEstimate: async (
    file: File,
    opts?: { projectId?: string; enableSketch?: boolean },
  ): Promise<ParsedEstimateResponse> => {
    const enq = await BackendAPI.enqueueParse(file, opts);

    // Poll until the job leaves "processing". Backoff 1 s → 3 s with a
    // generous cap (large imports take minutes). The server-side sweep
    // guarantees the job eventually resolves to failed if its worker died.
    const started = Date.now();
    const MAX_WAIT_MS = 15 * 60 * 1000;
    let delay = 1000;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const job = await BackendAPI.getJobStatus(enq.job_id);
      if (job.status === 'ready' && job.result) return job.result;
      if (job.status === 'failed') throw new Error(job.error || 'Import failed.');
      if (Date.now() - started > MAX_WAIT_MS) throw new Error('Import timed out.');
      await new Promise((r) => setTimeout(r, delay));
      delay = Math.min(delay + 500, 3000);
    }
  },

  /**
   * Run completeness analysis on an already-parsed estimate using the
   * parse_hash returned by parseEstimate — no re-upload required.
   */
  analyzeCachedEstimate: async (
    parseHash: string,
    claimType?: string,
    aggressiveness: string = 'moderate',
    jurisdiction?: string,
  ): Promise<CompletenessReport> => {
    return api.post('/completeness/analyze-cached', {
      parse_hash: parseHash,
      claim_type: claimType,
      aggressiveness,
      jurisdiction,
    });
  },

  /**
   * Search the canonical Xactimate dictionary by description substring.
   * Used by the verify-parse override UX so estimators can pick a code
   * for UNMAPPED rows.
   */
  searchDictionary: async (query: string): Promise<DictionarySearchResponse> => {
    return api.get('/dictionary/search', { params: { q: query } });
  },

  /**
   * Manually assign a CAT SEL to a line item in a cached parse, marking
   * its provenance as MANUAL. Returns the updated parsed estimate.
   */
  overrideLineItem: async (
    parseHash: string,
    roomIndex: number,
    lineItemIndex: number,
    cat: string,
    sel: string,
    replaceDescription = false,
    action?: string,
  ): Promise<ParsedEstimateResponse> => {
    return api.patch(
      `/completeness/parse/${encodeURIComponent(parseHash)}/line-item`,
      {
        room_index: roomIndex,
        line_item_index: lineItemIndex,
        cat,
        sel,
        replace_description: replaceDescription,
        ...baseVersion(parseHash),
        // B-511: only send `action` when explicitly chosen so the backend
        // leaves the wording-derived action untouched otherwise.
        ...(action !== undefined ? { action } : {}),
      },
    );
  },

  /**
   * B-486: assign one (cat, sel) to MANY line items at once — used by the
   * review screen's bundled duplicate-line picker. One request, one re-persist.
   */
  overrideLineItemsBulk: async (
    parseHash: string,
    targets: Array<{ roomIndex: number; lineItemIndex: number }>,
    cat: string,
    sel: string,
    replaceDescription = false,
    action?: string,
  ): Promise<ParsedEstimateResponse> => {
    return api.patch(
      `/completeness/parse/${encodeURIComponent(parseHash)}/line-items`,
      {
        targets: targets.map((t) => ({
          room_index: t.roomIndex,
          line_item_index: t.lineItemIndex,
        })),
        cat,
        sel,
        replace_description: replaceDescription,
        ...baseVersion(parseHash),
        ...(action !== undefined ? { action } : {}),
      },
    );
  },

  // --- B-511: cascade picker data (CAT → SEL → action) -------------------

  /** All Xactimate categories with their human group description. */
  listCategories: (): Promise<{ categories: CategoryOption[] }> =>
    api.get('/dictionary/categories'),

  /** Every selector in a category with description, default unit, and the
   * action symbols it supports — the SEL dropdown feed. */
  listSelectors: (cat: string): Promise<SelectorListResponse> =>
    api.get(`/dictionary/selectors/${encodeURIComponent(cat)}`),

  /** The action codes a (cat, sel) item supports, each a symbol + label.
   * Selectors can contain slashes (e.g. "1/2-"); the backend route is a
   * `:path` param, so keep the slash raw while encoding everything else. */
  listActions: (cat: string, sel: string): Promise<ActionListResponse> =>
    api.get(
      `/dictionary/actions/${encodeURIComponent(cat)}/${encodeURIComponent(sel).replace(/%2F/gi, '/')}`,
    ),

  // ----- Admin / RBAC ----------------------------------------------------

  /** Caller's identity + role set. Used by the sidebar to gate the Admin link. */
  adminMe: (): Promise<{ id: string; email: string; name: string; roles: string[] }> =>
    api.get('/admin/me'),

  adminUsageDaily: (params: { days?: number; user_id?: string } = {}) =>
    api.get('/admin/usage/daily', { params }) as Promise<{
      days: Array<{
        date: string;
        total_usd: number;
        by_model: Record<string, number>;
        by_provider: Record<string, number>;
      }>;
      total_usd: number;
    }>,

  adminUsageHourly: (params: { date?: string; user_id?: string } = {}) =>
    api.get('/admin/usage/hourly', { params }) as Promise<{
      date: string;
      hours: Array<{ hour: number; total_usd: number; by_model: Record<string, number> }>;
    }>,

  adminUsageByEndpoint: (params: { days?: number; user_id?: string } = {}) =>
    api.get('/admin/usage/by-endpoint', { params }) as Promise<{
      endpoints: Array<{
        endpoint: string;
        request_count: number;
        total_usd: number;
        avg_usd: number;
        p95_usd: number;
      }>;
    }>,

  adminListUsers: (params: { days?: number } = {}) =>
    api.get('/admin/users', { params }) as Promise<{
      window_days: number;
      users: Array<{
        id: string;
        email: string;
        name: string;
        is_active: boolean;
        created_at: string;
        roles: string[];
        project_count: number;
        spend_window_usd: number;
        spend_all_time_usd: number;
        credit_balance: number;
        credits_used: number;
        credits_purchased: number;
        amount_paid_usd: number;
        last_active: string | null;
        estimate_count: number;
        line_items_total: number;
        unmatched_total: number;
        error_count: number;
        health: 'ok' | 'look' | 'error' | 'none';
      }>;
    }>,

  /** Account/profile detail for the admin user card: activation, signup,
   *  subscription, credit balance + recent ledger. */
  adminUserAccount: (userId: string) =>
    api.get(`/admin/users/${encodeURIComponent(userId)}/account`) as Promise<{
      id: string;
      email: string | null;
      name: string | null;
      created_at: string | null;
      is_active: boolean;
      email_verified_at: string | null;
      signup_ip: string | null;
      signup_user_agent: string | null;
      credit_balance: number;
      subscription: {
        tier: string;
        status: string;
        current_period_start: string | null;
        current_period_end: string | null;
        stripe_customer_id: string | null;
      } | null;
      recent_ledger: Array<{
        delta: number;
        reason: string;
        ref: string | null;
        balance_after: number | null;
        created_at: string | null;
      }>;
    }>,

  /** Permanently delete a user and all their data. Super-admin only. */
  adminDeleteUser: (userId: string) =>
    api.delete(`/admin/users/${encodeURIComponent(userId)}`) as Promise<{
      user_id: string;
      deleted: boolean;
      projects_deleted: number;
    }>,

  adminSetUserRoles: (userId: string, roles: string[]) =>
    api.post(`/admin/users/${encodeURIComponent(userId)}/roles`, { roles }) as Promise<{
      user_id: string;
      roles: string[];
    }>,

  adminDeactivateUser: (userId: string) =>
    api.post(`/admin/users/${encodeURIComponent(userId)}/deactivate`) as Promise<{
      user_id: string;
      is_active: boolean;
    }>,

  adminActivateUser: (userId: string) =>
    api.post(`/admin/users/${encodeURIComponent(userId)}/activate`) as Promise<{
      user_id: string;
      is_active: boolean;
    }>,

  // ----- Lifecycle check-in email (B-968, admin) --------------------------

  /** Who is currently due a lifecycle check-in, with the exact body they'd
   *  receive. Purely a report — this endpoint never sends anything. */
  adminLifecycleDue: () =>
    api.get('/admin/lifecycle/due') as Promise<{
      email_enabled: boolean;
      due: Array<{
        user_id: string;
        email: string;
        template: string;
        reason: string;
        subject: string | null;
        html: string | null;
      }>;
    }>,

  /** Send to an EXPLICIT list of recipients. There is deliberately no
   *  "send to everyone due" call: the backend re-checks eligibility per item
   *  and caps the batch, and an operator has to name each recipient. */
  adminLifecycleSend: (items: Array<{ user_id: string; template: string }>) =>
    api.post('/admin/lifecycle/send', { items }) as Promise<{
      sent: number;
      skipped: number;
      failed: number;
      results: Array<{
        user_id: string;
        template: string;
        status: string;
        detail: string | null;
      }>;
    }>,

  // ----- Failed imports (PRP-IMPORT-FAIL-1, super-admin only) -------------

  /** Failed and degraded imports, newest first. The `error_excerpt` is capped
   *  server-side at 200 chars; the full text needs `adminFailure`. */
  adminListFailures: (
    params: {
      status?: string;
      severity?: string;
      user_id?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) =>
    api.get('/admin/failures', { params }) as Promise<{
      items: Array<{
        id: string;
        created_at: string | null;
        user_id: string;
        email: string | null;
        filename: string | null;
        stage: string;
        severity: string;
        error_class: string | null;
        error_excerpt: string | null;
        has_pdf: boolean;
        occurrences: number;
        status: string;
        backlog_ref: string | null;
        layout_guess: string | null;
        page_count: number | null;
        line_items: number | null;
        unmatched: number | null;
      }>;
      limit: number;
      offset: number;
      returned: number;
    }>,

  /** One failure in full, including the untruncated error text. */
  adminFailure: (failureId: string) =>
    api.get(`/admin/failures/${encodeURIComponent(failureId)}`) as Promise<{
      id: string;
      error_text: string | null;
      sha256: string | null;
      file_bytes: number | null;
      project_id: string | null;
      import_job_id: string | null;
      retention_until: string | null;
      [k: string]: unknown;
    }>,

  /** A 5-minute signed URL for the PDF that failed. 404s when the file was
   *  never retained — that is the honest answer, not a broken link. */
  adminFailurePdf: (failureId: string) =>
    api.get(`/admin/failures/${encodeURIComponent(failureId)}/pdf`) as Promise<{
      url: string;
      expires_in: number;
    }>,

  /** Move a failure through triage. Omitting `backlog_ref` leaves the existing
   *  one alone — it is never cleared by a status change. */
  adminSetFailureStatus: (
    failureId: string,
    status: string,
    backlogRef?: string,
  ) =>
    api.post(`/admin/failures/${encodeURIComponent(failureId)}/status`, {
      status,
      ...(backlogRef === undefined ? {} : { backlog_ref: backlogRef }),
    }) as Promise<{ id: string; status: string; backlog_ref: string | null }>,

  /** Stage this failure as a redacted fixture candidate under
   *  `tests/fixtures/intake/`. `redacted: false` means the PDF had no text
   *  layer and a REDACTION_NEEDED.md was written instead — the row is NOT
   *  marked promoted in that case. */
  adminPromoteFailure: (failureId: string) =>
    api.post(`/admin/failures/${encodeURIComponent(failureId)}/promote`, {}) as Promise<{
      fixture_id: string;
      path: string;
      redacted: boolean;
      replacements: number;
      unclassified: string[];
      files: string[];
    }>,

  // ----- Partner API management (B-608, super-admin only) -----------------

  /** All partners with a sanitized key list (prefix/env/status/last-used only —
   *  never a hash or full key). */
  adminListPartners: () =>
    api.get('/admin/partners') as Promise<{
      partners: Array<{
        id: string;
        name: string | null;
        contact_email: string | null;
        status: string;
        created_at: string | null;
        webhook_url: string | null;
        has_webhook_secret: boolean;
        keys: Array<{
          key_prefix: string;
          environment: string;
          status: string;
          created_at: string | null;
          last_used_at: string | null;
        }>;
      }>;
    }>,

  adminCreatePartner: (name: string, contactEmail: string) =>
    api.post('/admin/partners', { name, contact_email: contactEmail }) as Promise<{
      id: string;
      name: string | null;
      contact_email: string | null;
      status: string;
      created_at: string | null;
    }>,

  adminSetPartnerStatus: (partnerId: string, status: 'active' | 'suspended') =>
    api.post(`/admin/partners/${encodeURIComponent(partnerId)}/status`, { status }) as Promise<{
      id: string;
      status: string;
    }>,

  /** Set/clear a webhook. `generateSecret` mints + returns a new signing secret
   *  ONCE (in `webhook_secret`); `url: null` clears the webhook entirely. */
  adminSetPartnerWebhook: (
    partnerId: string,
    url: string | null,
    generateSecret: boolean,
  ) =>
    api.post(`/admin/partners/${encodeURIComponent(partnerId)}/webhook`, {
      url,
      generate_secret: generateSecret,
    }) as Promise<{
      id: string;
      webhook_url: string | null;
      webhook_secret?: string;
    }>,

  /** Create a key. `full_key` is returned exactly once — show it, never re-fetch. */
  adminCreatePartnerKey: (partnerId: string, environment: 'test' | 'live') =>
    api.post(`/admin/partners/${encodeURIComponent(partnerId)}/keys`, { environment }) as Promise<{
      full_key: string;
      key_prefix: string;
      environment: string;
      status: string;
      created_at: string | null;
    }>,

  adminRevokePartnerKey: (prefix: string) =>
    api.post('/admin/partners/keys/revoke', { prefix }) as Promise<{ revoked: number }>,

  // ----- Admin user activity (B-613, admin-only) --------------------------

  /** Per-user estimate history for the admin drill-down: parse/match quality,
   *  outcome, and error class for every estimate the user has touched.
   *  `public_ref` can be null for import_jobs-sourced failure rows. */
  adminUserEstimates: (userId: string) =>
    api.get(`/admin/users/${encodeURIComponent(userId)}/estimates`) as Promise<{
      estimates: Array<{
        public_ref: string | null;
        filename: string | null;
        created_at: string | null;
        line_items: number;
        room_count: number;
        matched: number;
        unmatched: number;
        confidence: number | null;
        likely_scanned: boolean;
        outcome: string;
        error_class: string | null;
        sketch_emitted: boolean;
        credits_charged: number;
      }>;
    }>,

  /** Drill-down detail for a single estimate: unmatched line items, parse
   *  diagnostics, and error info (when the estimate failed). */
  adminEstimateDetail: (publicRef: string) =>
    api.get(`/admin/estimates/${encodeURIComponent(publicRef)}/detail`) as Promise<{
      unmatched: Array<{
        description: string | null;
        room: string;
        reason: string;
        quantity: number | null;
        unit: string | null;
      }>;
      diagnostics: {
        headline?: string | null;
        pages_total?: number | null;
        pages_with_line_items?: number | null;
        pages_unprocessable?: number | null;
        confidence?: number | null;
        warnings?: string[];
      } | null;
      error: { class: string | null; message: string | null } | null;
    }>,

  /** A user's login history summary: total count, last login, and IPs seen. */
  adminUserLogins: (userId: string) =>
    api.get(`/admin/users/${encodeURIComponent(userId)}/logins`) as Promise<{
      count: number;
      last_login: string | null;
      ips: Array<{ ip: string | null; n: number; last_seen: string | null }>;
    }>,

  /** A user's credit standing for the admin panel (balance + recent ledger +
   *  any admin codes issued to them that are still unredeemed). */
  adminGetUserCredits: (userId: string) =>
    api.get(`/admin/users/${encodeURIComponent(userId)}/credits`) as Promise<{
      user_id: string;
      email: string | null;
      email_verified_at: string | null;
      balance: number;
      recent_ledger: Array<{
        delta: number;
        reason: string;
        ref: string | null;
        balance_after: number | null;
        created_at: string | null;
      }>;
      pending_codes: Array<{ code: string; credits: number; created_at: string | null }>;
    }>,

  /** Grant credits to a user. mode='direct' (default) applies them to the
   *  balance immediately (works for unverified users); mode='code' mints a
   *  redeemable code and emails it. */
  adminAddUserCredits: (
    userId: string,
    credits: number,
    mode: 'direct' | 'code' = 'direct',
    note?: string,
  ) =>
    api.post(`/admin/users/${encodeURIComponent(userId)}/credits`, {
      credits,
      mode,
      note,
    }) as Promise<{
      user_id: string;
      mode: string;
      code: string | null;
      credits: number;
      issued_to_email: string | null;
      emailed: boolean;
      balance: number;
    }>,

  /** Feedback inbox: list submitted feedback (filter by status to triage). */
  adminListFeedback: (params: { status?: string; limit?: number } = {}) =>
    api.get('/admin/feedback', { params }) as Promise<
      Array<{
        id: number;
        user_id: string;
        email: string | null;
        rating: number | null;
        would_pay: string | null;
        body: string | null;
        status: string;
        run_ref: string | null;
        created_at: string | null;
        reviewed_at: string | null;
      }>
    >,

  /** Resolve a feedback item: 'reward' grants credits directly, 'dismiss'
   *  marks it reviewed with no reward. */
  adminReviewFeedback: (
    feedbackId: number,
    action: 'reward' | 'dismiss',
    credits = 1,
  ) =>
    api.post(`/admin/feedback/${feedbackId}/review`, { action, credits }) as Promise<{
      id: number;
      status: string;
      credits_granted: number;
      balance: number | null;
    }>,

  /** Send a one-off email to a user. email_enabled=false means the provider
   *  is off and nothing was delivered regardless of address. */
  adminEmailUser: (userId: string, subject: string, body: string) =>
    api.post(`/admin/users/${encodeURIComponent(userId)}/email`, {
      subject,
      body,
    }) as Promise<{
      user_id: string;
      to_email: string | null;
      emailed: boolean;
      email_enabled: boolean;
    }>,

  adminListRoles: () =>
    api.get('/admin/roles') as Promise<{
      roles: Array<{ id: string; name: string; description: string }>;
    }>,

  adminPricing: () =>
    api.get('/admin/pricing') as Promise<{
      known_models: Record<string, string[]>;
      prices_per_mtok: Record<string, Record<string, { input: number; output: number; cached_input?: number }>>;
    }>,

  // --- PRP-010: per-user breakdown -----------------------------------

  adminUsageByUser: (params: { start?: string; end?: string; limit?: number } = {}) =>
    api.get('/admin/usage/by-user', { params }) as Promise<{
      users: Array<{
        user_id: string;
        email: string | null;
        name: string | null;
        total_cost_usd: number;
        request_count: number;
        last_request_at: string;
        daily_sparkline: number[];
      }>;
      window: { start: string; end: string; days: number };
      total_users: number;
      total_cost_usd: number;
    }>,

  adminUsageByUserDetail: (userId: string, params: { start?: string; end?: string } = {}) =>
    api.get(`/admin/usage/by-user/${encodeURIComponent(userId)}`, { params }) as Promise<{
      user: { id: string; email: string | null; name: string | null };
      window: { start: string; end: string; days: number };
      total_cost_usd: number;
      total_requests: number;
      by_day: Array<{ date: string; total_usd: number; by_model: Record<string, number> }>;
      by_endpoint: Array<{ endpoint: string; request_count: number; total_usd: number; avg_usd: number }>;
      by_model: Array<{ model: string; total_usd: number; input_tokens: number; output_tokens: number }>;
      recent_calls: Array<{
        ts: string;
        endpoint: string | null;
        model: string;
        cost_usd: number;
        input_tokens: number;
        output_tokens: number;
      }>;
    }>,

  // --- PRP-010: thresholds + alerts ----------------------------------

  adminListThresholds: () =>
    api.get('/admin/thresholds') as Promise<{
      thresholds: Array<{
        id: string;
        scope: 'per_user' | 'global';
        user_id: string | null;
        user_email: string | null;
        metric: string;
        threshold_value: number;
        action: 'alert' | 'soft_block' | 'hard_block';
        enabled: boolean;
        created_at: string;
        updated_at: string;
      }>;
    }>,

  adminCreateThreshold: (payload: {
    scope: 'per_user' | 'global';
    user_id?: string | null;
    metric: string;
    threshold_value: number;
    action: 'alert' | 'soft_block' | 'hard_block';
    enabled?: boolean;
  }) =>
    api.post('/admin/thresholds', payload) as Promise<{ id: string }>,

  adminUpdateThreshold: (
    id: string,
    payload: Partial<{
      threshold_value: number;
      action: 'alert' | 'soft_block' | 'hard_block';
      enabled: boolean;
    }>,
  ) =>
    api.patch(`/admin/thresholds/${encodeURIComponent(id)}`, payload) as Promise<{
      ok: boolean;
    }>,

  adminDeleteThreshold: (id: string) =>
    api.delete(`/admin/thresholds/${encodeURIComponent(id)}`) as Promise<{ ok: boolean }>,

  adminEvaluateThresholds: () =>
    api.post('/admin/thresholds/evaluate') as Promise<{
      evaluated_at: string;
      thresholds_checked: number;
      alerts_created: number;
    }>,

  adminListAlerts: (params: { acknowledged?: boolean; limit?: number } = {}) =>
    api.get('/admin/alerts', { params }) as Promise<{
      alerts: Array<{
        id: string;
        threshold_id: string;
        user_id: string | null;
        user_email: string | null;
        metric: string;
        metric_value: number;
        threshold_value: number;
        action_taken: string;
        triggered_at: string;
        acknowledged_at: string | null;
        acknowledged_by: string | null;
        resolved_at: string | null;
      }>;
      unacknowledged_count: number;
    }>,

  adminAcknowledgeAlert: (id: string) =>
    api.post(`/admin/alerts/${encodeURIComponent(id)}/acknowledge`) as Promise<{ ok: boolean }>,

  adminUnblockUser: (userId: string) =>
    api.post(`/admin/users/${encodeURIComponent(userId)}/unblock`) as Promise<{
      user_id: string;
      cleared_alerts: number;
    }>,

  adminBlockedUsers: () =>
    api.get('/admin/blocked-users') as Promise<{
      blocked: Array<{
        user_id: string;
        email: string | null;
        action: 'soft_block' | 'hard_block';
        triggered_at: string;
        threshold_id: string;
      }>;
    }>,

  // --- PDF→ESX trial (Phase 3) ---
  meBootstrap: (referralCode?: string | null): Promise<BootstrapResponse> =>
    api.post('/me/bootstrap', referralCode ? { referral_code: referralCode } : undefined), // PRP-MCP-10
  acceptAgreement: (): Promise<AgreementResponse> => api.post('/me/accept-agreement'),
  getCredits: (): Promise<CreditsResponse> => api.get('/me/credits'),

  // --- Credit codes + feedback (pay-per-conversion) ---
  redeemCode: (code: string) =>
    api.post('/me/redeem-code', { code }) as Promise<{
      status: 'ok' | 'already_redeemed_self';
      balance: number;
      credits: number | null;
    }>,
  ...grantsApi(api), // admin per-user feature grants (PRP-MCP-7)
  ...meApi(api), // pending codes, feedback, B-7202 export dialog + summary email

  // --- Stored imports (the durable artifact a paid /parse creates) ---
  listImports: (projectId?: string) =>
    api.get('/imports/', {
      params: projectId ? { project_id: projectId } : undefined,
    }) as Promise<ImportSummary[]>,
  getImport: (parseHash: string): Promise<ParsedEstimateResponse> =>
    api.get(`/imports/${encodeURIComponent(parseHash)}`),
  // PRP-MCP-5 G9: the import as it arrived, before any edit (read only). 404 = none stored.
  getImportOriginal: (parseHash: string): Promise<ParsedEstimateResponse> =>
    api.get(`/imports/${encodeURIComponent(parseHash)}/original`),
  // B-600: original uploaded source PDF, retained server-side, for the
  // "View in source PDF" viewer on reopened/background imports. The response
  // interceptor unwraps to response.data, which with responseType 'blob' IS
  // the Blob. 404 = no blob retained (legacy import) — caller hides the viewer.
  getImportSourcePdf: (parseHash: string): Promise<Blob> =>
    api.get(`/imports/${encodeURIComponent(parseHash)}/source-pdf`, {
      responseType: 'blob',
    }),
  linkImportToProject: (parseHash: string, projectId: string | null) =>
    api.post(`/imports/${encodeURIComponent(parseHash)}/link-project`, {
      project_id: projectId,
    }) as Promise<{ parse_hash: string; project_id: string | null }>,
  deleteImport: (parseHash: string) =>
    api.delete(`/imports/${encodeURIComponent(parseHash)}`) as Promise<{
      parse_hash: string;
      deleted: boolean;
    }>,

  // --- Stripe billing (inline-priced credit packs) ---
  // Prices come from the backend so the UI display always matches what Stripe charges.
  getBillingPacks: () =>
    api.get('/billing/packs') as Promise<{
      enabled: boolean;
      packs: Array<{ id: string; credits: number; amount_cents: number; label: string }>;
    }>,
  createCheckoutSession: (packId: string) =>
    api.post('/billing/create-checkout-session', { pack_id: packId }) as Promise<{ url: string }>,
  // PRP-MCP-9: the pricing config and the Pro / Pro Plus checkout are in ./plansApi.
  // Opens the Stripe customer portal so a subscriber can manage/cancel.
  createPortalSession: () =>
    api.post('/billing/create-portal-session', {}) as Promise<{ url: string }>,
  // Success-page handoff: returns the purchase's credit code without waiting
  // for the backup email (verified against Stripe; idempotent with the webhook).
  claimPurchase: (sessionId: string) =>
    api.get(`/billing/session/${encodeURIComponent(sessionId)}/claim`) as Promise<{
      status: 'ready' | 'pending';
      code: string | null;
      credits: number | null;
      redeemed: boolean;
    }>,

  // --- Enhance Scope v2 (B-531) — base path /api/v1/enhance ---------------

  /**
   * Hydrate/resume the enhance session for a parse. Returns null when the
   * feature flag is off (or the hash isn't owned) — the 404 is the feature
   * detector for the "Enhance" mode, matching the optional-feature pattern
   * used by getScopeDraft / getLatestProjectScope (return null, don't throw).
   *
   * Args:
   *   parseHash (string): the parse_hash the Supplement page is showing.
   *
   * Returns:
   *   Promise<EnhanceSessionResponse | null>: hydrated session (narratives,
   *   latest run, applied ids), or null when the feature is unavailable.
   */
  getEnhanceSession: async (
    parseHash: string,
  ): Promise<EnhanceSessionResponse | null> => {
    try {
      return await api.get(`/enhance/parse/${encodeURIComponent(parseHash)}`);
    } catch {
      // Reason: 404 = feature off / hash not owned. Any failure hides the beta
      // feature rather than crashing the import page.
      return null;
    }
  },

  /** Debounced autosave of narratives + loss context. Free (no LLM). */
  saveEnhanceSession: (
    parseHash: string,
    body: { narratives: EnhanceNarratives; context: EnhanceContext },
  ): Promise<{ ok: boolean; updated_at: string }> =>
    api.put(`/enhance/parse/${encodeURIComponent(parseHash)}/session`, body),

  /**
   * Run the enhancement engine → proposal queue.
   *
   * mode 'deterministic' (default) = templates + companions + loss context, no
   * LLM, no credit. mode 'full' = interpret the saved narratives with one LLM
   * call, then run deterministic on top.
   *
   * When the server-side LLM feature flag is off, a 'full' run rejects with a
   * 400 whose plain-English message the interceptor surfaces on `err.message`
   * (with `err.status === 400`). Callers should catch that and offer the
   * deterministic run instead.
   *
   * Args:
   *   parseHash (string): the parse_hash the Supplement page is showing.
   *   mode ('deterministic' | 'full'): interpretation mode. Defaults to
   *     'deterministic'.
   *
   * Returns:
   *   Promise<EnhanceRun>: the run with its proposal queue.
   */
  runEnhance: (
    parseHash: string,
    mode: 'deterministic' | 'full' = 'deterministic',
  ): Promise<EnhanceRun> =>
    api.post(`/enhance/parse/${encodeURIComponent(parseHash)}/run`, { mode }),

  /**
   * Apply the user's triage decisions. Returns the updated parse (same
   * ParsedEstimateResponse shape overrideLineItem returns) so the page can
   * replace state WYSIWYG, plus which proposals applied vs. were skipped.
   */
  applyEnhance: (
    parseHash: string,
    resolutions: EnhanceResolution[],
  ): Promise<EnhanceApplyResponse> =>
    api.post(`/enhance/parse/${encodeURIComponent(parseHash)}/apply`, {
      resolutions,
    }),

  /** Revert previously-applied proposals by id (deletes ADDs, restores state). */
  revertEnhance: (
    parseHash: string,
    proposalIds: string[],
  ): Promise<EnhanceRevertResponse> =>
    api.post(`/enhance/parse/${encodeURIComponent(parseHash)}/revert`, {
      proposal_ids: proposalIds,
    }),

  // --- Roof interview + live scope preview (PRP-ROOF-2 S2) ----------------

  /**
   * Run the deterministic roof interview + generator against manually-entered
   * measurements and answers-so-far. Stateless: nothing is persisted. Always
   * returns the visible questions so the UI can re-render the form; `result`
   * is null with a structured `error` when the selected system can't generate
   * yet (repair mode, non-asphalt families).
   */
  roofInterviewPreview: (
    payload: RoofInterviewPreviewRequest,
  ): Promise<RoofPreviewResponse> =>
    api.post('/roof/interview/preview', payload),

  /**
   * Parse an uploaded EagleView roof-report XML into RoofMeasurements
   * (PRP-ROOF-1). Stateless; the UI feeds the result into the interview.
   */
  roofParseMeasurements: (file: File): Promise<RoofMeasurementsParseResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/roof/interview/measurements/parse', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Default export so stores/tests can `import BackendAPI from '../lib/api'`.
export default BackendAPI;

export interface DictionarySearchResult {
  id?: string;
  cat: string;
  sel: string;
  activity: string;
  activity_code: string;
  description: string;
  unit: string;
  group_code?: string | null;
  group_description?: string | null;
  notes?: string;
  is_user_priced?: boolean;
}

export interface DictionarySearchResponse {
  results: DictionarySearchResult[];
  count: number;
}

// --- B-511: cascade picker (CAT → SEL → action) ---

/** A category row for the cascade's CAT dropdown. */
export interface CategoryOption {
  cat: string;
  group_code?: string | null;
  group_description?: string | null;
  count?: number;
}

/** A selector row for the cascade's SEL dropdown — description, default unit,
 * and the action symbols this item supports. */
export interface SelectorOption {
  sel: string;
  dsc: string;
  unit: string;
  actions: string[];
}

export interface SelectorListResponse {
  cat: string;
  selectors: SelectorOption[];
  count: number;
}

/** An action row for the cascade's ACTION dropdown — symbol + human label. */
export interface ActionOption {
  symbol: string;
  label: string;
}

export interface ActionListResponse {
  cat: string;
  sel: string;
  actions: ActionOption[];
  count: number;
}

// --- Roof interview + live scope preview (PRP-ROOF-2 S2) ---

/** One row of the areas-per-pitch table (pitch as rise string, e.g. '6/12'). */
export interface RoofPitchArea {
  pitch: string;
  area_sf: number;
}

/** Linear-foot measurements from the report summary. All LFs default 0. */
export interface RoofLengths {
  ridge_lf: number;
  hip_lf: number;
  valley_lf: number;
  rake_lf: number;
  eave_lf: number;
  drip_edge_lf: number;
  flashing_lf: number;
  step_flashing_lf: number;
  parapet_lf: number;
}

/** Optional waste block — only `suggested_pct` is entered manually. */
export interface RoofWaste {
  suggested_pct?: number | null;
}

/** Per-facet row from a parsed measurement report (enables facet selection). */
export interface RoofFacet {
  label: string;
  area_sf: number;
  pitch: string;
}

/** RoofMeasurements v1 — the measurement-report contract (manual entry or
 * a parsed report; report-only fields are optional). */
export interface RoofMeasurements {
  source: 'manual' | 'eagleview' | 'gaf_quickmeasure' | 'roofr' | 'aerialreports' | 'other';
  total_area_sf: number;
  predominant_pitch: string;
  stories: number;
  areas_by_pitch: RoofPitchArea[];
  lengths: RoofLengths;
  waste?: RoofWaste | null;
  report_id?: string | null;
  property_address?: string | null;
  facet_count?: number | null;
  facets?: RoofFacet[] | null;
  source_document_ref?: string | null;
}

/** Response of POST /roof/interview/measurements/parse. */
export interface RoofMeasurementsParseResponse {
  measurements: RoofMeasurements;
  /** Vendor the report was parsed as: "eagleview" | "hover" | "roofr" | ... */
  source: string;
  /** "full"/"lean" for EagleView XML (published vs geometry-derived); null
   * for every other format (PDFs, Hover XML). */
  dialect: 'full' | 'lean' | null;
  /** True when the source file was persisted server-side (B-739);
   * measurements.source_document_ref then carries the durable object key. */
  stored?: boolean;
}

/** Request body for POST /roof/interview/preview. */
export interface RoofInterviewPreviewRequest {
  measurements: RoofMeasurements;
  answers: Record<string, unknown>;
  zip_code?: string;
  state?: string | null;
}

/** A single interview question, as returned (already visibility-filtered). */
export interface RoofQuestion {
  id: string;
  label: string;
  type: 'single_select' | 'multi_select' | 'count' | 'boolean';
  options?: string[];
  options_from?: string;
  default?: unknown;
  min?: number;
  group?: string;
  help?: string;
  ui_hints?: Record<string, unknown>;
}

/** One catalog-bound roof line item in the preview result. */
export interface RoofLineItem {
  rule_id: string;
  binding_key: string;
  cat: string;
  sel: string;
  dsc: string;
  unit: string;
  activity?: string | null;
  quantity: number;
  section: string;
  review_required: boolean;
  estimated_proration: boolean;
  notes?: string | null;
}

/** Full generator output for one roof. */
export interface RoofScopeResult {
  line_items: RoofLineItem[];
  template_id: string;
  template_version: number;
  profile_id: string;
  damage_mode: string;
  warnings: string[];
}

/** Structured error when the selected system can't generate a scope yet. */
export interface RoofPreviewError {
  code: 'repair_not_supported' | 'template_not_available' | 'generator_error';
  message: string;
}

/** Response body from POST /roof/interview/preview. */
export interface RoofPreviewResponse {
  profile_id: string;
  questions: RoofQuestion[];
  effective_answers: Record<string, unknown>;
  system?: Record<string, unknown> | null;
  result?: RoofScopeResult | null;
  error?: RoofPreviewError | null;
}

export type { AdminFeatureGrant } from './grantsApi';
